import type { StatField } from '../binding-resolver/transforms';
import { isAccreditationHidden } from '../accreditationVisibility';

/**
 * Response-boundary normalizer for the organizations-service rankings list
 * (`POST /v1/rankings/{listKey}`). It pre-derives the composite arrays a
 * `RankingCard` renders — `marketing.stats`, `marketing.details`, and
 * `marketing.badges` — from the flat, keyed marketing payload the service
 * returns (`marketing.institutionalStats`, `marketing.tags`,
 * `marketing.location`, `marketing.additional`).
 *
 * It also emits `marketing.statFields`: a display-ready keyed map
 * (`slug → { label, value, tooltip? }`, curated labels + formatted values)
 * covering every metric present. This is the config-driven successor to the
 * fixed `stats` / `details` arrays — a DSL config binds `_bind.stats` /
 * `_bind.details` to this map and selects/orders entries via a per-binding
 * `keys` list, so tiles and detail rows can curate independent metric sets.
 * It is additive: the arrays above are left intact for the deployed config.
 *
 * Why here (mirrors {@link normalizeOfferingsMatchItem} /
 * `buildOfferingsMatchResponseCoercer`): the service exposes
 * `institutionalStats` as a metric-slug → `{ label, value }` map, not the
 * ordered `StatField[]` the card consumes, and it carries no notion of which
 * metrics belong in the top stat tiles vs. the detail grid vs. the badge row.
 * That curation + formatting is legacy-card knowledge (the old
 * `RankingsAdapter` `buildDataPanel` / `buildGeneralInfo`), not a general DSM
 * concern, so it lives in the bridge and runs on the raw response before
 * `responsePath` extraction. A DSL config then binds the derived arrays with
 * the `stats` / `badges` transforms exactly like any organic array.
 *
 * This is an interim seam; the durable fix is service-/BFF-side derivation of
 * these arrays — retire this when that lands. See the rankings DE parity-gaps
 * doc for the residual formatting/label deltas vs. legacy.
 *
 * Pure and total: sponsored items (no `marketing`) and organic items with a
 * thin/empty `marketing` pass through untouched (no empty arrays stamped), and
 * a malformed payload is returned unchanged.
 */

/**
 * Curated display labels per institutional-stat key, matching the legacy
 * rankings card (`packages/features/src/content/shortcodes/RankingsShortcode/
 * utils.ts` `buildDataPanel` / `buildGeneralInfo`). These take precedence over
 * the service-supplied `entry.label` so the new DE surface reads with the same
 * curated copy as the legacy `[rankings]` shortcode it replaces. Keys absent
 * here fall back to `entry.label`, then the raw key.
 */
const LEGACY_STAT_LABELS: Record<string, string> = {
  percentOnlineEnrollment: 'Online Enrollment',
  percentAdmittedUndergrad: 'Acceptance Rate',
  graduationRateTotalCohort: 'Graduation Rate',
  fulltimeRetentionRate: 'Retention Rate',
  instateAvgTuitionFulltimeUndergrad: 'In-State Tuition',
  avgNetPriceStudentsAwardedAid: 'Avg Net Price',
  enrollmentTotal: 'Students Enrolled',
  studentToFacultyRatio: 'Student-to-Faculty Ratio',
  medianDebtCompletersOverall: 'Median Debt',
  accreditator: 'Accreditation',
};

/**
 * Label overrides applied ONLY to the config-bindable `statFields` map (see
 * {@link deriveStatFieldMap}), NOT to the deployed `stats` / `details` arrays.
 * These match the exact tuition copy the rankings pages render
 * ("Avg. Undergrad Tuition, In-State / Out-of-State") rather than the shorter
 * array-panel copy in {@link LEGACY_STAT_LABELS}. Kept separate so opting a
 * config into `statFields` does not change the labels of the array-bound config
 * that is live today. Keys absent here inherit {@link LEGACY_STAT_LABELS}, then
 * the service `entry.label`, then the raw key.
 *
 * Tuition keys are spelled under the service's EXACT `institutionalStats`
 * casing (confirmed against the organizations-service enrichment schema):
 * `instateAvgTuitionFulltimeUndergrad` (lowercase `instate`),
 * `outOfStateAvgTuitionFulltimeUndergrad`, `inStateAvgTuitionFulltimeGrad`,
 * and `outOfStateAvgTuitionFulltimeGrad` (camelCase). The undergrad in-state
 * key is lowercase while the other three are camelCase — that inconsistency
 * is the service's, mirrored here on purpose (the prior lowercase
 * `outofstate…` spelling never matched and silently dropped the row). Grad
 * rows only appear once the enrichment schema exposes them (org-service
 * #139) and a config binds/gates them; absent data is simply omitted.
 */
const STAT_FIELD_LABELS: Record<string, string> = {
  ...LEGACY_STAT_LABELS,
  instateAvgTuitionFulltimeUndergrad: 'Avg. Undergrad Tuition, In-State',
  outOfStateAvgTuitionFulltimeUndergrad: 'Avg. Undergrad Tuition, Out-of-State',
  inStateAvgTuitionFulltimeGrad: 'Avg. Graduate Tuition, In-State',
  outOfStateAvgTuitionFulltimeGrad: 'Avg. Graduate Tuition, Out-of-State',
};

/** Top stat tiles, in render order (legacy 4-tile panel). */
const STAT_KEYS = [
  'percentOnlineEnrollment',
  'percentAdmittedUndergrad',
  'graduationRateTotalCohort',
  'fulltimeRetentionRate',
] as const;

/** Secondary detail rows, in render order (legacy general-info panel). */
const DETAIL_KEYS = [
  'instateAvgTuitionFulltimeUndergrad',
  'avgNetPriceStudentsAwardedAid',
  'enrollmentTotal',
  'studentToFacultyRatio',
  'medianDebtCompletersOverall',
  'accreditator',
] as const;

const PERCENT_KEYS = new Set<string>([
  'percentOnlineEnrollment',
  'percentAdmittedUndergrad',
  'graduationRateTotalCohort',
  'fulltimeRetentionRate',
]);

const CURRENCY_KEYS = new Set<string>([
  'instateAvgTuitionFulltimeUndergrad',
  'outOfStateAvgTuitionFulltimeUndergrad',
  'inStateAvgTuitionFulltimeGrad',
  'outOfStateAvgTuitionFulltimeGrad',
  'avgNetPriceStudentsAwardedAid',
  'medianDebtCompletersOverall',
]);

const COUNT_KEYS = new Set<string>(['enrollmentTotal']);

const RATIO_KEYS = new Set<string>(['studentToFacultyRatio']);

type InstitutionalStat = { label?: unknown; value?: unknown };

export function normalizeRankingsResponse(res: unknown): unknown {
  if (!isPlainObject(res) || !Array.isArray(res.items)) {
    return res;
  }
  let changed = false;
  const items = res.items.map((item) => {
    const next = normalizeItem(item);
    if (next !== item) changed = true;
    return next;
  });
  return changed ? { ...res, items } : res;
}

function normalizeItem(item: unknown): unknown {
  if (!isPlainObject(item)) return item;
  const marketing = item.marketing;
  if (!isPlainObject(marketing)) return item;

  const stats = deriveStatFields(marketing.institutionalStats, STAT_KEYS);
  const details = deriveStatFields(marketing.institutionalStats, DETAIL_KEYS);
  const badges = deriveBadges(marketing);
  // Display-ready keyed map (all present metrics), for configs that bind
  // `_bind.stats` / `_bind.details` straight to it and curate/order via a
  // per-binding `keys` list — the config-driven successor to the fixed
  // `STAT_KEYS` / `DETAIL_KEYS` arrays above. Derived AFTER `deriveBadges`,
  // which reads the RAW numeric `institutionalStats` values, so the formatted
  // strings here never leak into badge derivation.
  const statFields = deriveStatFieldMap(marketing.institutionalStats);

  // Only stamp fields that have content so a thin organic item (and every
  // sponsored item, which has no `marketing`) is a genuine no-op rather than
  // getting empty arrays that would render an empty grid.
  if (
    stats.length === 0 &&
    details.length === 0 &&
    badges.length === 0 &&
    statFields === undefined
  ) {
    return item;
  }

  const nextMarketing: Record<string, unknown> = { ...marketing };
  if (stats.length > 0) nextMarketing.stats = stats;
  if (details.length > 0) nextMarketing.details = details;
  if (badges.length > 0) nextMarketing.badges = badges;
  if (statFields !== undefined) nextMarketing.statFields = statFields;
  return { ...item, marketing: nextMarketing };
}

/**
 * Project a curated, ordered key list out of `institutionalStats` into
 * `StatField[]`. Labels resolve from {@link LEGACY_STAT_LABELS} first, then the
 * service-supplied `entry.label`, then the raw key; values are formatted per
 * key category (percent / currency / count / ratio), with pre-formatted string
 * values passing through. Keys absent from the payload are skipped, so the
 * output length tracks whatever the school actually has.
 */
function deriveStatFields(
  institutionalStats: unknown,
  keys: readonly string[],
): StatField[] {
  if (!isPlainObject(institutionalStats)) return [];
  const out: StatField[] = [];
  for (const key of keys) {
    const raw = institutionalStats[key];
    if (!isPlainObject(raw)) continue;
    const entry = raw as InstitutionalStat;
    if (entry.value === null || entry.value === undefined) continue;
    const label =
      LEGACY_STAT_LABELS[key] ??
      (typeof entry.label === 'string' && entry.label.length > 0
        ? entry.label
        : key);

    if (key === 'showAccreditation') continue;

    if (key === 'accreditator') {
      if (isAccreditationHidden(institutionalStats)) continue;
      // Same string-only predicate as `hasAccreditation` (the badge gate), so
      // the detail row and the "Accredited" chip never diverge for a
      // non-string accreditator value.
      const name = readString(entry.value);
      if (name === null) continue;
      out.push({ label, value: 'Yes', tooltip: name });
      continue;
    }

    out.push({ label, value: formatStatValue(key, entry.value) });
  }
  return out;
}

/**
 * Build a display-ready keyed stat map (`slug → { label, value, tooltip? }`)
 * from `institutionalStats`, covering every metric the school actually has —
 * not just the fixed {@link STAT_KEYS} / {@link DETAIL_KEYS} sets. Labels are
 * curated via {@link STAT_FIELD_LABELS} (then service `entry.label`, then the
 * raw key) and values formatted per key category, so a DSL config can bind
 * `RankingCard.stats` / `.details` to this map and pick/order tiles vs. rows
 * independently through a per-binding `keys` list (see `BindDeclaration.keys`).
 *
 * Accreditation is emitted in both legacy forms so a config can render either
 * (or both) independently: `accreditator` is the affirmative chip
 * (`value: 'Yes'`, accreditor name in the tooltip), and the synthetic
 * `accreditatorName` key is the detail-row form (`value` = the accreditor name,
 * e.g. "Higher Learning Commission"). `accreditatorName` is not a service slug;
 * a config opts into the name row explicitly via its `keys` list.
 *
 * Additive to the pre-built `marketing.stats` / `marketing.details` arrays:
 * this leaves those untouched so the currently deployed array-bound config
 * keeps rendering. Returns `undefined` (no field stamped) when no metric
 * resolves to a renderable entry.
 */
function deriveStatFieldMap(
  institutionalStats: unknown,
): Record<string, StatField> | undefined {
  if (!isPlainObject(institutionalStats)) return undefined;
  const out: Record<string, StatField> = {};
  for (const [key, raw] of Object.entries(institutionalStats)) {
    if (!isPlainObject(raw)) continue;
    const entry = raw as InstitutionalStat;
    if (entry.value === null || entry.value === undefined) continue;
    const label =
      STAT_FIELD_LABELS[key] ??
      (typeof entry.label === 'string' && entry.label.length > 0
        ? entry.label
        : key);

    if (key === 'showAccreditation') continue;

    if (key === 'accreditator') {
      if (isAccreditationHidden(institutionalStats)) continue;
      const name = readString(entry.value);
      if (name === null) continue;
      out[key] = { label, value: 'Yes', tooltip: name };
      out.accreditatorName = { label, value: name };
      continue;
    }

    out[key] = { label, value: formatStatValue(key, entry.value) };
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function formatStatValue(key: string, value: unknown): string {
  // Pre-formatted strings (e.g. `"65%"`) pass through — the service may emit
  // either raw numbers or display strings depending on the source metric.
  if (typeof value === 'string') return value;
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return String(value);
  }
  if (PERCENT_KEYS.has(key)) return `${Math.round(value)}%`;
  if (CURRENCY_KEYS.has(key)) {
    return `$${Math.round(value).toLocaleString('en-US')}`;
  }
  if (COUNT_KEYS.has(key)) return value.toLocaleString('en-US');
  if (RATIO_KEYS.has(key)) return `${value}:1`;
  return String(value);
}

/**
 * Badge row (legacy general-info chips): `"City, ST"`, program length,
 * campus/online modality, and an `"Accredited"` affirmative chip. Each chip is
 * emitted only when its source data is present, so a school missing location,
 * level, or accreditation simply gets fewer chips. The `"Accredited"` chip
 * mirrors legacy `RankingsAdapter` (`if (school.accreditation)
 * badges.push('Accredited')`) and is additive to the existing `accreditator`
 * detail row, which is left untouched.
 */
function deriveBadges(marketing: Record<string, unknown>): string[] {
  const badges: string[] = [];

  const location = readLocationBadge(marketing);
  if (location) badges.push(location);

  const duration = readDurationBadge(marketing);
  if (duration) badges.push(duration);

  const modality = readModalityBadge(marketing);
  if (modality) badges.push(modality);

  if (hasAccreditation(marketing)) badges.push('Accredited');

  return badges;
}

function hasAccreditation(marketing: Record<string, unknown>): boolean {
  const stats = isPlainObject(marketing.institutionalStats)
    ? marketing.institutionalStats
    : undefined;
  if (!stats || isAccreditationHidden(stats)) return false;
  const entry = stats.accreditator;
  if (!isPlainObject(entry)) return false;
  return readString((entry as InstitutionalStat).value) !== null;
}

function readLocationBadge(marketing: Record<string, unknown>): string | null {
  const tags = isPlainObject(marketing.tags) ? marketing.tags : undefined;
  const location = isPlainObject(marketing.location)
    ? marketing.location
    : undefined;
  const city = readString(tags?.city) ?? readString(location?.city);
  const state = readString(tags?.state) ?? readString(location?.state);
  if (city && state) return `${city}, ${state}`;
  return city ?? state ?? null;
}

function readDurationBadge(marketing: Record<string, unknown>): string | null {
  const additional = isPlainObject(marketing.additional)
    ? marketing.additional
    : undefined;
  const stats = isPlainObject(marketing.institutionalStats)
    ? marketing.institutionalStats
    : undefined;
  const levelStat =
    stats && isPlainObject(stats.institutionLevel)
      ? readString((stats.institutionLevel as InstitutionalStat).value)
      : undefined;
  const level = readString(additional?.levelOfInstitution) ?? levelStat;
  if (!level) return null;
  return mapInstitutionLevel(level);
}

/**
 * Map an IPEDS `levelOfInstitution` string to duration badge copy, matching the
 * legacy rankings cards (`getDuration` in
 * `packages/features/src/content/shortcodes/RankingsShortcode/utils.ts`).
 *
 * The phrase is matched before the bare-token heuristic because the 2-year
 * value ("At least 2 but less than 4 years") contains a standalone `4`: testing
 * `4` first labelled every community college as 4-year. Phrase matching is also
 * the only way to reach `"<2 year"`, and it absorbs the schema's wording
 * variants (e.g. "Less than 2 years (below associate)") that an exact-equality
 * map would miss.
 *
 * Fails closed like legacy `getDuration` (`default: return ''`): anything not
 * recognized as a program length yields no chip rather than being emitted
 * verbatim. `levelOfInstitution` is unvalidated free text, so the unrecognized
 * bucket is where non-levels land — IPEDS missing-data sentinels (`{Not
 * available}`, `{Not applicable}` on branch-campus shells) and incidental
 * strings alike. A genuine level always carries one of the phrases or a
 * standalone `2`/`4` token, so failing closed drops only non-levels.
 */
function mapInstitutionLevel(level: string): string | null {
  const normalized = level.trim().toLowerCase().replace(/\s+/g, ' ');
  if (normalized.startsWith('less than 2 years')) return '<2 year';
  // A range names the institution by its LOWER bound.
  if (normalized.includes('less than 4')) return '2 year';
  if (normalized.includes('four or more')) return '4 year';
  // Only a standalone `4`/`four` (or `2`/`two`) token reads as a duration, so an
  // incidental digit (`"24-month"`, a ZIP, an id) is not mistaken for one.
  if (/\b(?:2|two)\b/.test(normalized)) return '2 year';
  if (/\b(?:4|four)\b/.test(normalized)) return '4 year';
  return null;
}

/**
 * Bucket a school as Campus / Campus + Online / Online, matching the legacy
 * rankings cards (`getSchoolType` in
 * `packages/features/src/content/shortcodes/RankingsShortcode/utils.ts`).
 *
 * Legacy bucketed on the share of *programs* offered online.
 * `percentOnlineEnrollment` is the share of *students* enrolled online — a
 * genuinely different metric that lands in a different bucket for roughly a
 * third of the schools carrying both, so it is only a fallback for the few
 * schools missing the programs share.
 *
 * There is deliberately no `tags.isOnline` fallback. Legacy `getSchoolType`
 * derived the chip solely from a percentage and returned `''` (no chip) when it
 * was absent — it never consulted a boolean. `isOnline` defaults to `false` on
 * the IPEDS shell records that lack a percentage (108 in the corpus, the same
 * branch-campus class as `{Not available}` levels), so honoring it would stamp
 * a confident "Campus" onto schools we have no modality data for. Returning
 * `null` restores parity: no data, no chip.
 */
function readModalityBadge(marketing: Record<string, unknown>): string | null {
  const additional = isPlainObject(marketing.additional)
    ? marketing.additional
    : undefined;
  const percent =
    // The programs share arrives only via institutionalStats; `additional` is
    // an explicitly enumerated DTO and does not carry it. `??` (not `||`) is
    // required: both helpers return `null` (never `0`) on absence, so a genuine
    // `0` share (a Campus-only school) must fall through to the buckets below
    // rather than be treated as falsy and skipped to the next fallback.
    readStatNumber(marketing.institutionalStats, 'percentOnlinePrograms') ??
    readNumber(additional?.percentOnlineEnrollment) ??
    readStatNumber(marketing.institutionalStats, 'percentOnlineEnrollment');
  if (percent === null) return null;
  if (percent >= 100) return 'Online';
  if (percent <= 0) return 'Campus';
  return 'Campus + Online';
}

function readStatNumber(
  institutionalStats: unknown,
  key: string,
): number | null {
  if (!isPlainObject(institutionalStats)) return null;
  const entry = institutionalStats[key];
  if (!isPlainObject(entry)) return null;
  return readNumber((entry as InstitutionalStat).value);
}

function readString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function readNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
